import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, Download, RefreshCw, Copy, Monitor, Server, CheckCircle2, XCircle, Clock, Power } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { PrintHistory } from "@/components/PrintHistory";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }) : "—");
const STATUS = { pending: ["In coda", Clock, "text-amber-400"], printing: ["In stampa", Printer, "text-blue-400"], done: ["Stampata", CheckCircle2, "text-emerald-400"], failed: ["Errore", XCircle, "text-destructive"] };

function Step({ n, children }) {
  return (
    <li className="flex gap-3 text-sm">
      <span className="h-6 w-6 shrink-0 rounded-full bg-primary/15 text-primary grid place-items-center text-xs font-bold">{n}</span>
      <div className="space-y-1">{children}</div>
    </li>
  );
}

const Code = ({ children }) => <code className="block rounded-md bg-slate-950 border border-border px-3 py-2 text-xs font-mono-code text-emerald-300 whitespace-pre-wrap break-all">{children}</code>;

const ORIGIN = window.location.origin;
const WIN_CMD = `"C:\\Program Files\\Google\\Chrome\\Application\\chrome.exe" --kiosk --kiosk-printing "${ORIGIN}/gioca"`;
const MAC_CMD = `osascript -e 'quit app "Google Chrome"'; sleep 2; open -na "Google Chrome" --args --kiosk --kiosk-printing "${ORIGIN}/gioca"`;

function downloadMacLauncher() {
  const script = `#!/bin/bash\n# Vivi Win · avvia il totem in Chrome con stampa automatica (kiosk-printing)\nosascript -e 'quit app "Google Chrome"'\nsleep 2\nopen -na "Google Chrome" --args --kiosk --kiosk-printing "${ORIGIN}/gioca"\n`;
  const url = URL.createObjectURL(new Blob([script], { type: "text/x-shellscript" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "Avvia Totem.command" });
  a.click();
  URL.revokeObjectURL(url);
}

function ChromeKioskGuide() {
  return (
    <Card className="p-5 bg-card border-border space-y-4" data-testid="chrome-kiosk-guide">
      <h2 className="font-display font-bold text-lg flex items-center gap-2"><Monitor className="h-5 w-5 text-primary" /> A · Stampa dal browser del totem (consigliata)</h2>
      <p className="text-sm text-muted-foreground">Funziona con qualsiasi stampante installata sul PC del totem (es. Ricoh MP C2003). Nessun programma da installare.</p>
      <ol className="space-y-3">
        <Step n={1}><p>Nel concorso imposta <b>Stampante → Browser</b> e attiva <b>Stampa automatica alla vincita</b>.</p></Step>
        <Step n={2}><p>Sul computer del totem imposta la Ricoh come <b>stampante predefinita</b> (Windows: Impostazioni → Stampanti · Mac: Impostazioni di Sistema → Stampanti e scanner → "Stampante predefinita") e nel driver scegli formato <b>A5</b> (o A4), verticale.</p></Step>
        <Step n={3}><p>Avvia Chrome con la modalità kiosk-printing (stampa senza finestra di dialogo). <b>Importante: Chrome deve essere completamente chiuso prima</b> (Windows: chiudi tutte le finestre · Mac: <kbd>Cmd+Q</kbd>), altrimenti il flag viene ignorato e compare la finestra di stampa.</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs font-semibold">Windows · collegamento sul desktop</p>
              <Code>{WIN_CMD}</Code>
            </div>
            <div className="space-y-1">
              <p className="text-xs font-semibold">Mac · Terminale (o scarica l'avviatore)</p>
              <Code>{MAC_CMD}</Code>
              <Button size="sm" variant="secondary" className="gap-2" data-testid="mac-launcher-download" onClick={downloadMacLauncher}><Download className="h-4 w-4" /> Scarica "Avvia Totem.command"</Button>
              <p className="text-[11px] text-muted-foreground">Dopo il download: tasto destro → Apri (la prima volta macOS chiede conferma in Privacy e sicurezza). Chiude Chrome e lo riapre in kiosk-printing.</p>
            </div>
          </div></Step>
        <Step n={4}><p>Verifica subito la configurazione con il tasto qui sotto (da aprire <b>nel Chrome del totem</b>): con kiosk-printing attivo la pagina di prova esce senza finestra di dialogo; se invece compare la finestra di stampa, il flag non è attivo (torna al punto 3). Se non compare nulla e non esce nulla, controlla la coda della stampante predefinita.</p>
          <Button size="sm" variant="secondary" className="gap-2" data-testid="browser-test-print-button" onClick={() => window.print()}><Printer className="h-4 w-4" /> Stampa pagina di prova dal browser</Button>
          <div id="print-receipt" className="hidden print:block p-8 text-slate-900">
            <h1 className="text-2xl font-bold">VIVI WIN · Stampa di prova</h1>
            <p className="mt-2">Se leggi questo foglio, Chrome kiosk-printing e la stampante predefinita sono configurati correttamente.</p>
            <p className="mt-2 text-sm">Accenti: perché città più € · {new Date().toLocaleString("it-IT")} · formato A5</p>
          </div></Step>
        <Step n={5}><p>Fai una giocata vincente di prova: la ricevuta A5 esce da sola e sotto la ricevuta compare "Stampa automatica inviata". Se compare "Stampa automatica disattivata", attiva lo switch nel concorso.</p></Step>
      </ol>
    </Card>
  );
}

const MAC_AUTOSTART_SCRIPT = `#!/bin/bash
# Vivi Win · avvio automatico del totem all'accensione del Mac
sleep 15
osascript -e 'quit app "Google Chrome"'
sleep 2
open -na "Google Chrome" --args --kiosk --kiosk-printing --noerrdialogs --disable-session-crashed-bubble "${ORIGIN}/gioca"
`;

function downloadMacAutostart() {
  const url = URL.createObjectURL(new Blob([MAC_AUTOSTART_SCRIPT], { type: "text/x-shellscript" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: "Totem Autostart.command" });
  a.click();
  URL.revokeObjectURL(url);
}

function MacAutostartGuide() {
  return (
    <Card className="p-5 bg-card border-border space-y-4" data-testid="mac-autostart-guide">
      <h2 className="font-display font-bold text-lg flex items-center gap-2"><Power className="h-5 w-5 text-primary" /> A2 · Avvio automatico del totem (Mac)</h2>
      <p className="text-sm text-muted-foreground">Quando si accende il Mac, il totem parte da solo in Chrome kiosk-printing: nessun intervento del personale.</p>
      <ol className="space-y-3">
        <Step n={1}><p><b>Login automatico</b>: Impostazioni di Sistema → Utenti e gruppi → "Accedi automaticamente come" → scegli l'utente del totem (richiede di disattivare FileVault in Privacy e sicurezza).</p></Step>
        <Step n={2}><p><b>Scarica lo script di avvio</b> e spostalo in una cartella fissa (es. <i>Documenti</i>). Al primo avvio: tasto destro → Apri → consenti in Privacy e sicurezza. Attende 15 s che la rete sia pronta, poi apre Chrome in kiosk.</p>
          <Code>{MAC_AUTOSTART_SCRIPT.trim()}</Code>
          <Button size="sm" variant="secondary" className="gap-2" data-testid="mac-autostart-download" onClick={downloadMacAutostart}><Download className="h-4 w-4" /> Scarica "Totem Autostart.command"</Button>
          <p className="text-[11px] text-muted-foreground">Se lo script non è eseguibile, nel Terminale: <code className="font-mono-code">chmod +x ~/Documents/"Totem Autostart.command"</code></p></Step>
        <Step n={3}><p><b>Aggiungi agli Elementi login</b>: Impostazioni di Sistema → Generali → Elementi login → "+" → seleziona <i>Totem Autostart.command</i>. Rimuovi Chrome se già presente in elenco (deve avviarlo lo script, non macOS).</p></Step>
        <Step n={4}><p><b>Mai in stop</b>: Impostazioni di Sistema → Schermo di blocco → "Disattiva lo schermo quando inattivo" = Mai · "Richiedi password dopo" = Mai. Poi in Batteria/Risparmio energia (o Terminale): <code className="font-mono-code">sudo pmset -a sleep 0 displaysleep 0 disksleep 0</code></p></Step>
        <Step n={5}><p><b>Accensione programmata</b> (opzionale): il Mac si accende e si spegne da solo negli orari del negozio. Nel Terminale, es. lun–sab ore 8:30 / spegnimento 20:30:</p>
          <Code>{`sudo pmset repeat wakeorpoweron MTWRFS 08:30:00 shutdown MTWRFS 20:30:00`}</Code></Step>
        <Step n={6}><p><b>Riavvio dopo blackout</b>: Terminale → <code className="font-mono-code">sudo pmset -a autorestart 1</code>. Infine riavvia il Mac per verificare: il totem deve comparire da solo a schermo intero.</p></Step>
      </ol>
    </Card>
  );
}

function AgentGuide() {
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const load = () => {
    api.printAgentStatus().then(setStatus).catch(() => {});
    api.printAgentJobs(storeId).then(setJobs).catch(() => {});
  };
  useEffect(() => {
    api.printAgentToken().then(setInfo).catch(() => {});
    api.listStores().then(setStores).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);
  const rotate = async () => {
    if (!window.confirm("Rigenerare il token? Gli agenti già configurati smetteranno di funzionare finché non aggiorni il comando.")) return;
    setInfo(await api.printAgentRotateToken());
    toast.success("Token rigenerato");
  };
  const cmd = info ? `python vivi_print_agent.py --server ${info.server_url} --token ${info.token} --printer-ip 192.168.1.50 --printer-port 9100` : "…";
  const online = status?.last_seen_at && Date.now() - new Date(status.last_seen_at).getTime() < 60000;

  return (
    <Card className="p-5 bg-card border-border space-y-4" data-testid="agent-guide">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display font-bold text-lg flex items-center gap-2 flex-1"><Server className="h-5 w-5 text-primary" /> B · Agente di stampa locale (termiche ESC/POS e stampanti di rete)</h2>
        <span className={`text-xs font-semibold rounded-full px-3 py-1 border ${online ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-secondary text-muted-foreground border-border"}`} data-testid="agent-status">
          {online ? "Agente online" : `Agente offline · ultimo contatto ${fmt(status?.last_seen_at)}`}
        </span>
      </div>
      <p className="text-sm text-muted-foreground">Il server Vivi Win è nel cloud e non può raggiungere le stampanti sulla rete del negozio. L'agente è un piccolo programma (Python, senza dipendenze) che gira su un PC del negozio: scarica le ricevute da stampare e le invia alla stampante sulla porta 9100.</p>
      <ol className="space-y-3">
        <Step n={1}><p>Nel concorso imposta <b>Termica (IP)</b> oppure <b>Ufficio PDF (IP)</b> con l'IP della stampante in negozio.</p></Step>
        <Step n={2}><p>Su un PC del negozio (sempre acceso) installa Python 3 da python.org e scarica l'agente:</p>
          <a href={api.printAgentDownloadUrl()} data-testid="agent-download-button"><Button size="sm" variant="secondary" className="gap-2"><Download className="h-4 w-4" /> Scarica vivi_print_agent.py</Button></a></Step>
        <Step n={3}><p>Avvialo con questo comando (sostituisci l'IP della stampante; aggiungi <code>--store-id</code> per stampare solo le ricevute di una sede):</p>
          <div className="flex gap-2 items-start">
            <div className="flex-1"><Code>{cmd}</Code></div>
            <Button size="icon" variant="ghost" data-testid="agent-copy-button" onClick={() => { navigator.clipboard.writeText(cmd); toast.success("Comando copiato"); }}><Copy className="h-4 w-4" /></Button>
          </div>
          <Button size="sm" variant="ghost" className="gap-1.5 text-xs" data-testid="agent-rotate-button" onClick={rotate}><RefreshCw className="h-3.5 w-3.5" /> Rigenera token</Button></Step>
        <Step n={4}><p>Prova con <b>Stampa di prova</b> nel concorso o con una vincita: la ricevuta compare qui sotto e viene stampata entro pochi secondi.</p></Step>
      </ol>
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
        <label className="text-sm font-semibold">Filtra per sede</label>
        <select data-testid="print-store-filter" value={storeId} onChange={(e) => setStoreId(e.target.value)} className="h-9 rounded-md border border-border bg-background px-3 text-sm">
          <option value="">Tutte le sedi</option>
          {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      <PrintHistory storeId={storeId} />
      <div>
        <h3 className="text-sm font-semibold mb-2">Ultime ricevute in coda <span className="text-muted-foreground font-normal">· {status?.pending ?? 0} in attesa, {status?.failed ?? 0} in errore</span></h3>
        {jobs.length === 0 ? <p className="text-xs text-muted-foreground" data-testid="agent-jobs-empty">Nessuna ricevuta inviata all'agente.</p> : (
          <div className="divide-y divide-border/60 text-sm" data-testid="agent-jobs">
            {jobs.slice(0, 15).map((j) => { const [l, I, c] = STATUS[j.status] || STATUS.pending; return (
              <div key={j.id} className="py-2 flex items-center gap-3">
                <I className={`h-4 w-4 shrink-0 ${c}`} />
                <span className="font-mono-code text-xs">{j.receipt_serial}</span>
                <span className="text-xs text-muted-foreground flex-1 truncate">{j.contest_name} · {j.store_name || "—"} · {j.format.toUpperCase()} → {j.printer_ip}</span>
                <span className={`text-xs ${c}`}>{l}</span>
                <span className="text-xs text-muted-foreground">{fmt(j.printed_at || j.created_at)}</span>
                {j.status === "failed" && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => api.printAgentRetry(j.id).then(load)}>Riprova</Button>}
              </div>); })}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function PrintGuide() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/80 border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <Button data-testid="print-guide-back" variant="ghost" size="icon" onClick={() => navigate("/")}><ArrowLeft className="h-5 w-5" /></Button>
          <Logo className="h-10" />
          <div><h1 className="font-display font-bold text-xl">Guida alla stampa delle ricevute</h1><p className="text-xs text-muted-foreground">Totem · stampanti da ufficio · termiche</p></div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-5" data-testid="print-guide-page">
        <ChromeKioskGuide />
        <MacAutostartGuide />
        <AgentGuide />
      </main>
    </div>
  );
}
